import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { OnboardingLayout } from '../../components/onboarding/OnboardingLayout';
import { SelectionCard } from '../../components/SelectionCard';
import { MultiSelectChips } from '../../components/ui/MultiSelectChips';
import { Toggle } from '../../components/ui/Toggle';
import { OrganizerProfile } from '../../types/profile';
const STEPS = [{
  id: 'basics',
  title: 'Event Basics',
  description: 'Key event details'
}, {
  id: 'audience',
  title: 'Audience Profile',
  description: 'Who attends?'
}, {
  id: 'needs',
  title: 'Sponsorship Needs',
  description: 'What do you need?'
}, {
  id: 'media',
  title: 'Media & Exposure',
  description: 'Reach and coverage'
}, {
  id: 'logistics',
  title: 'Logistics',
  description: 'Space and requirements'
}, {
  id: 'summary',
  title: 'Review',
  description: 'Confirm event details'
}];
const ATTENDANCE_RANGES = ['Under 100', '100 - 500', '500 - 1,000', '1,000 - 5,000', '5,000+'];
const AUDIENCE_TYPES = ['Students', 'Professionals', 'Families', 'Tech Enthusiasts', 'Creatives', 'Local Community', 'VIPs'];
const AGE_GROUPS = ['Gen Z (18-24)', 'Millennials (25-40)', 'Gen X (41-56)', 'Boomers (57+)'];
const SPONSOR_CATEGORIES = ['Food & Beverage', 'Tech', 'Fashion', 'Health', 'Financial', 'Any'];
const OFFERINGS = ['Logo Placement', 'Booth Space', 'Speaking Opportunity', 'Social Media Shoutout', 'Product Sampling', 'VIP Access'];
const ACTIVATION_SPACES = ['Small (Table)', 'Medium (10x10)', 'Large (20x20+)', 'Multiple Locations', 'Not Sure'];
export function OrganizerOnboarding() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<Partial<OrganizerProfile>>({
    eventName: '',
    description: '',
    eventDate: '',
    location: '',
    attendeeCount: '',
    audienceTypes: [],
    audienceAgeGroups: [],
    desiredSponsorCategories: [],
    sponsorOfferings: [],
    sponsorshipFeatures: [],
    socialMediaLinks: [],
    digitalReach: '',
    pressCoverage: 'No',
    activationSpace: ''
  });
  const updateData = (key: keyof OrganizerProfile, value: any) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(prev => prev + 1);
    } else {
      console.log('Submitting Organizer Profile:', formData);
      navigate('/dashboard/organizer');
    }
  };
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        // Basics
        return <div className="space-y-6" data-id="element-2992">
            <h3 className="text-xl font-bold text-gray-900" data-id="element-2993">Event Basics</h3>
            <div data-id="element-2994">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2995">
                Event Name
              </label>
              <input type="text" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" value={formData.eventName} onChange={e => updateData('eventName', e.target.value)} data-id="element-2996" />
            </div>
            <div data-id="element-2997">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2998">
                Description
              </label>
              <textarea className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" rows={3} value={formData.description} onChange={e => updateData('description', e.target.value)} data-id="element-2999" />
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-3000">
              <div data-id="element-3001">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3002">
                  Date
                </label>
                <input type="date" className="w-full px-4 py-2 border border-gray-300 rounded-lg" value={formData.eventDate} onChange={e => updateData('eventDate', e.target.value)} data-id="element-3003" />
              </div>
              <div data-id="element-3004">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3005">
                  Location (City)
                </label>
                <input type="text" className="w-full px-4 py-2 border border-gray-300 rounded-lg" placeholder="e.g. New York, NY" value={formData.location} onChange={e => updateData('location', e.target.value)} data-id="element-3006" />
              </div>
            </div>
            <div data-id="element-3007">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3008">
                Expected Attendance
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-id="element-3009">
                {ATTENDANCE_RANGES.map(range => <button key={range} onClick={() => updateData('attendeeCount', range)} className={`px-3 py-2 rounded-lg text-sm border ${formData.attendeeCount === range ? 'bg-indigo-50 border-indigo-500 text-indigo-700' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'}`} data-id="element-3010">
                    {range}
                  </button>)}
              </div>
            </div>
          </div>;
      case 2:
        // Audience
        return <div className="space-y-8" data-id="element-3011">
            <div data-id="element-3012">
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-3013">
                Audience Types
              </h3>
              <MultiSelectChips options={AUDIENCE_TYPES} selected={formData.audienceTypes || []} onChange={selected => updateData('audienceTypes', selected)} data-id="element-3014" />
            </div>
            <div data-id="element-3015">
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-3016">
                Age Groups
              </h3>
              <MultiSelectChips options={AGE_GROUPS} selected={formData.audienceAgeGroups || []} onChange={selected => updateData('audienceAgeGroups', selected)} data-id="element-3017" />
            </div>
          </div>;
      case 3:
        // Needs
        return <div className="space-y-8" data-id="element-3018">
            <div data-id="element-3019">
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-3020">
                Sponsor Categories Needed
              </h3>
              <MultiSelectChips options={SPONSOR_CATEGORIES} selected={formData.desiredSponsorCategories || []} onChange={selected => updateData('desiredSponsorCategories', selected)} data-id="element-3021" />
            </div>
            <div data-id="element-3022">
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-3023">
                What can sponsors get?
              </h3>
              <MultiSelectChips options={OFFERINGS} selected={formData.sponsorOfferings || []} onChange={selected => updateData('sponsorOfferings', selected)} data-id="element-3024" />
            </div>
          </div>;
      case 4:
        // Media
        return <div className="space-y-6" data-id="element-3025">
            <h3 className="text-xl font-bold text-gray-900" data-id="element-3026">
              Media & Exposure
            </h3>
            <div data-id="element-3027">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3028">
                Social Media Handles
              </label>
              <input type="text" className="w-full px-4 py-2 border border-gray-300 rounded-lg" placeholder="@instagram, @twitter (comma separated)" value={formData.socialMediaLinks?.join(', ')} onChange={e => updateData('socialMediaLinks', e.target.value.split(',').map(s => s.trim()))} data-id="element-3029" />
            </div>
            <div data-id="element-3030">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3031">
                Digital Reach (Followers/Subscribers)
              </label>
              <select className="w-full px-4 py-2 border border-gray-300 rounded-lg" value={formData.digitalReach} onChange={e => updateData('digitalReach', e.target.value)} data-id="element-3032">
                <option value="" data-id="element-3033">Select range...</option>
                <option value="< 1k" data-id="element-3034">Under 1k</option>
                <option value="1k - 10k" data-id="element-3035">1k - 10k</option>
                <option value="10k - 50k" data-id="element-3036">10k - 50k</option>
                <option value="50k+" data-id="element-3037">50k+</option>
              </select>
            </div>
            <div data-id="element-3038">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3039">
                Press Coverage
              </label>
              <div className="flex gap-4" data-id="element-3040">
                {['Yes', 'No', 'Occasional'].map(opt => <label key={opt} className="flex items-center gap-2" data-id="element-3041">
                    <input type="radio" name="press" checked={formData.pressCoverage === opt} onChange={() => updateData('pressCoverage', opt)} className="text-indigo-600 focus:ring-indigo-500" data-id="element-3042" />
                    <span className="text-gray-700" data-id="element-3043">{opt}</span>
                  </label>)}
              </div>
            </div>
          </div>;
      case 5:
        // Logistics
        return <div className="space-y-6" data-id="element-3044">
            <h3 className="text-xl font-bold text-gray-900" data-id="element-3045">Logistics</h3>
            <div data-id="element-3046">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3047">
                Activation Space Available
              </label>
              <div className="grid grid-cols-2 gap-3" data-id="element-3048">
                {ACTIVATION_SPACES.map(space => <SelectionCard key={space} id={space} label={space} isSelected={formData.activationSpace === space} onClick={() => updateData('activationSpace', space)} className="p-3" data-id="element-3049" />)}
              </div>
            </div>
            <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg text-center" data-id="element-3050">
              <p className="text-sm text-gray-500" data-id="element-3051">
                Floor plan upload (Optional)
              </p>
              <button className="mt-2 text-indigo-600 text-sm font-medium" data-id="element-3052">
                Choose file
              </button>
            </div>
          </div>;
      case 6:
        // Summary
        return <div className="space-y-6" data-id="element-3053">
            <h3 className="text-xl font-bold text-gray-900" data-id="element-3054">Review Event</h3>
            <div className="bg-gray-50 rounded-xl p-6 space-y-4" data-id="element-3055">
              <SummarySection title="Event Basics" onEdit={() => setCurrentStep(1)} data-id="element-3056">
                <p className="font-medium" data-id="element-3057">{formData.eventName}</p>
                <p className="text-sm text-gray-500" data-id="element-3058">
                  {formData.eventDate} • {formData.location}
                </p>
              </SummarySection>

              <SummarySection title="Audience" onEdit={() => setCurrentStep(2)} data-id="element-3059">
                <div className="flex flex-wrap gap-1" data-id="element-3060">
                  {formData.audienceTypes?.map(t => <span key={t} className="text-xs bg-white border px-2 py-1 rounded" data-id="element-3061">
                      {t}
                    </span>)}
                </div>
              </SummarySection>

              <SummarySection title="Sponsorship" onEdit={() => setCurrentStep(3)} data-id="element-3062">
                <p className="text-sm text-gray-600" data-id="element-3063">
                  Offering: {formData.sponsorOfferings?.join(', ')}
                </p>
              </SummarySection>

              <SummarySection title="Logistics" onEdit={() => setCurrentStep(5)} data-id="element-3064">
                <p className="text-sm text-gray-600" data-id="element-3065">
                  Space: {formData.activationSpace}
                </p>
              </SummarySection>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <OnboardingLayout currentStep={currentStep} totalSteps={STEPS.length} steps={STEPS} title="Event Setup" description="Create your event profile to find sponsors." onBack={handleBack} onNext={handleNext} nextLabel={currentStep === STEPS.length ? 'Publish Event' : 'Next'} data-id="element-3066">
      {renderStepContent()}
    </OnboardingLayout>;
}
function SummarySection({
  title,
  children,
  onEdit
}: {
  title: string;
  children: React.ReactNode;
  onEdit: () => void;
}) {
  return <div className="border-b border-gray-200 pb-4 last:border-0 last:pb-0" data-id="element-3067">
      <div className="flex justify-between items-center mb-2" data-id="element-3068">
        <h4 className="text-sm font-semibold text-gray-900" data-id="element-3069">{title}</h4>
        <button onClick={onEdit} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium" data-id="element-3070">
          Edit
        </button>
      </div>
      {children}
    </div>;
}